import React from 'react';
import { Table, Flex, Tag, Button, Input, Space } from 'antd';
import { stringToMoneyFormat } from 'utils';
import {
  PACKAGING_METHOD,
  PRODUCT_STATUS,
  PRODUCT_TYPE,
} from 'constants/ProductConstants';
import { Content, Title } from 'components';
import { DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';

const MENU_TYPE = {
  GENERAL: {
    label: '음료',
    color: 'default',
  },
  DESSERT: {
    label: '디저트',
    color: 'default',
  },
  MD: {
    label: 'MD',
    color: 'default',
  },
};

const CouponProductTable = (props) => {
  /* ===== Route ===== */
  /* ===== Props ===== */
  const {
    type = 'menu',
    data = [],
    categories = [],
    shop,
    selected,
    onSelect,
    style = {},
  } = props;

  /* ===== State ===== */
  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');
  const searchInput = React.useRef(null);

  const [isShow, setIsShow] = React.useState(false);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  /* ===== Hooks ===== */
  /**
   * 테이블 컬럼 설정
   * --
   */
  const columns = React.useMemo(() => {
    const col = {
      menu: [
        {
          title: '메뉴번호',
          key: 'menu_id',
          dataIndex: 'menu_id',
          sorter: (a, b) => a?.menu_id - b?.menu_id,
          fixed: 'left',
        },
        {
          title: '카테고리번호',
          key: 'product_category_id',
          dataIndex: 'product_category_id',
          sorter: (a, b) => a?.product_category_id - b?.product_category_id,
        },
        {
          title: '카테고리 명',
          key: 'product_category_name',
          dataIndex: 'product_category_name',
          filters: categories?.map((_item) => ({
            text: _item?.product_category_name,
            value: _item?.product_category_id,
          })),
          onFilter: (value, record) => record.product_category_id === value,
        },
        {
          title: '메뉴명',
          key: 'menu_name',
          dataIndex: 'menu_name',
          ...getColumnSearchProps('menu_name'),
        },
        {
          title: '메뉴 설명',
          key: 'menu_content',
          dataIndex: 'menu_content',
          render: (item) =>
            item?.length > 20 ? `${item.substring(0, 20)}...` : item,
        },
        {
          title: '판매가',
          key: 'price',
          dataIndex: 'price',
          render: (item) => stringToMoneyFormat(item, '원'),
          sorter: (a, b) => a?.price - b?.price,
        },
        {
          title: '메뉴타입',
          key: 'type',
          dataIndex: 'type',
          render: (item) => (
            <Tag color={MENU_TYPE[item]?.color}>{MENU_TYPE[item]?.label}</Tag>
          ),
        },
        {
          title: '노출여부',
          key: 'is_show',
          dataIndex: 'is_show',
          filters: [
            { text: '노출됨', value: 'Y' },
            { text: '미노출', value: 'N' },
          ],
          onFilter: (value, record) => record.is_show === value,
        },
      ],
      product: [
        {
          title: '상품번호',
          key: 'product_id',
          dataIndex: 'product_id',
          sorter: (a, b) => a?.product_id - b?.product_id,
          fixed: 'left',
        },
        {
          title: '카테고리번호',
          key: 'product_category_id',
          dataIndex: 'product_category_id',
          sorter: (a, b) => a?.product_category_id - b?.product_category_id,
        },
        {
          title: '카테고리 명',
          key: 'product_category_name',
          dataIndex: 'product_category_name',
          filters: categories?.map((_item) => ({
            text: _item?.product_category_name,
            value: _item?.product_category_id,
          })),
          onFilter: (value, record) => record.product_category_id === value,
        },
        {
          title: '상품명',
          key: 'product_name',
          dataIndex: 'product_name',
          fixed: 'left',
          ...getColumnSearchProps('product_name'),
        },
        {
          title: '상품코드',
          key: 'product_code',
          dataIndex: 'product_code',
          ...getColumnSearchProps('product_code'),
        },
        {
          title: '업체 번호',
          key: 'shop_information_id',
          dataIndex: 'shop_information_id',
          sorter: (a, b) => a?.shop_information_id - b?.shop_information_id,
        },
        {
          title: '업체 명',
          key: 'shop_information_name',
          dataIndex: 'shop_information_name',
          filters: shop?.map((_item) => ({
            text: _item?.shop_information_name,
            value: _item?.shop_information_id,
          })),
          onFilter: (value, record) => record.shop_information_id === value,
        },
        {
          title: '판매가',
          key: 'price',
          dataIndex: 'price',
          render: (item) => stringToMoneyFormat(item, '원'),
          sorter: (a, b) => a?.price - b?.price,
        },
        {
          title: '상품타입',
          key: 'type',
          dataIndex: 'type',
          render: (item) => (
            <Tag color={PRODUCT_TYPE[item]?.color}>
              {PRODUCT_TYPE[item]?.title}
            </Tag>
          ),
          filters: Object.entries(PRODUCT_TYPE)
            ?.filter(([key, value]) => key !== 'RENTAL')
            ?.map(([key, value]) => ({
              text: value?.title,
              value: key,
            })),
          onFilter: (value, record) => record.type === value,
        },
        {
          title: '노출여부',
          key: 'is_show',
          dataIndex: 'is_show',
          filters: [
            {
              text: '노출',
              value: 'Y',
            },
            {
              text: '숨김',
              value: 'N',
            },
          ],
          onFilter: (value, record) => record.is_show === value,
        },
        {
          title: '포장타입',
          key: 'packaging_method',
          dataIndex: 'packaging_method',
          render: (item) => (
            <Tag color={PACKAGING_METHOD[item]?.color}>
              {PACKAGING_METHOD[item]?.title}
            </Tag>
          ),
          filters: Object.entries(PACKAGING_METHOD)?.map(([key, value]) => ({
            text: value?.title,
            value: key,
          })),
          onFilter: (value, record) => record.packaging_method === value,
        },
        {
          title: '판매 상태',
          key: 'product_status',
          dataIndex: 'product_status',
          render: (item) => (
            <Tag color={PRODUCT_STATUS[item]?.color}>
              {PRODUCT_STATUS[item]?.title}
            </Tag>
          ),
          filters: Object.entries(PRODUCT_STATUS)?.map(([key, value]) => ({
            text: value?.title,
            value: key,
          })),
          onFilter: (value, record) => record.product_status === value,
        },
        {
          title: '선물가능여부',
          key: 'is_present',
          dataIndex: 'is_present',
        },
      ],
    };

    return col[type];
  }, [type, categories, shop]);

  /* ===== Function ===== */
  /* ===== Variable ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    subTitle: {
      fontSize: 17,
      fontWeight: 500,
      lineHeight: '141%',
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  /* ===== Hooks ===== */
  /* ===== Render ===== */
  return (
    <Content
      maxWidth={'100%'}
      padding={20}
      borderRadius={4}
      style={{ ...style }}
    >
      <Title style={{ ...styles.subTitle }}>
        적용 {type === 'menu' ? '메뉴' : '상품'} 선택
      </Title>

      <Flex style={{ marginTop: 14 }} align="center" justify="space-between">
        <p>
          선택 상품 <span style={{ color: '#1677FF' }}>{selected?.length}</span>
          개
        </p>

        <Button
          onClick={() => setIsShow(!isShow)}
          icon={isShow ? <UpOutlined /> : <DownOutlined />}
          iconPosition={'end'}
          size="small"
          type="text"
        >
          선택상품 보기
        </Button>
      </Flex>

      {/* 선택상품 목록 */}
      {isShow ? (
        <div style={{ margin: '10px 0px', padding: 15, background: '#F4F4F5' }}>
          <Flex style={{ flexWrap: 'wrap' }} gap={4}>
            {selected?.length
              ? selected.map((item, index) => (
                  <Tag
                    key={`selected-${item[`${type}_id`]}-${index}`}
                    closable
                    onClose={() => onSelect('unchecked', item)}
                    style={{ margin: 0 }}
                  >
                    {item[`${type}_name`]}
                  </Tag>
                ))
              : null}
          </Flex>
        </div>
      ) : null}

      {/* 선택상품 목록 끝 */}

      {/* 적용상품 테이블 */}
      <div style={{ marginTop: 5 }}>
        <Table
          dataSource={data}
          columns={columns}
          rowSelection={{
            type: 'checkbox',
            onSelect: (record, selected, selectedRows, nativeEvent) => {
              onSelect(selected ? 'checked' : 'unchecked', record);
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              onSelect(
                selected ? 'all-checked' : 'all-unchecked',
                selectedRows
              );
            },
            selectedRowKeys: selected?.map((_item) => _item?.key),
          }}
        />
      </div>
      {/* 적용상품 테이블 끝 */}
    </Content>
  );
};

export default CouponProductTable;
